<template>
  <lb-dialog 
    data-testid="revenue-breakup-detailed-information"
    v-model="internalValue" 
    heading="Revenue Breakup - Detailed Information" 
    :width="1200"
  >
    <template v-slot:body>
      <div v-if="selectedRevenueDetail" data-testid="revenue-breakup-detailed-information__content" class="pa-4">
        <!-- Subscription Name Header -->
        <v-card data-testid="revenue-breakup-detailed-information__header-card" class="mb-4 elevation-3 header-card">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <v-icon data-testid="revenue-breakup-detailed-information__header-icon" size="32" color="primary" class="mr-3">mdi-briefcase-account</v-icon>
              <div>
                <div class="caption grey--text">Subscription Name</div>
                <div data-testid="revenue-breakup-detailed-information__client-name" class="font-weight-medium primary--text">{{ selectedRevenueDetail.client_name || '-' }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Financial Metrics Card -->
        <v-card data-testid="revenue-breakup-detailed-information__financial-metrics-card" class="mb-4 elevation-2">
          <v-card-title class="success white--text py-3">
            <v-icon data-testid="revenue-breakup-detailed-information__financial-metrics-icon" class="mr-2" color="white">mdi-cash-multiple</v-icon>
            Financial Metrics
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__actual-amount-card" outlined class="pa-3 text-center metric-card-success">
                  <div class="caption grey--text">Actual Amount</div>
                  <div data-testid="revenue-breakup-detailed-information__actual-amount-value" class="title font-weight-bold success--text">{{ formatCurrency(selectedRevenueDetail._raw_actual_amount || selectedRevenueDetail.actual_amount) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__proforma-amount-card" outlined class="pa-3 text-center metric-card-proforma">
                  <div class="caption grey--text">{{ proformaShortformLabel }} Amount</div>
                  <div data-testid="revenue-breakup-detailed-information__proforma-amount-value" class="title font-weight-bold">{{ formatCurrency(selectedRevenueDetail._raw_proforma_amount || selectedRevenueDetail.proforma_amount) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__forecast-amount-card" outlined class="pa-3 text-center metric-card-forecast">
                  <div class="caption grey--text">{{ forecastShortformLabel }} Amount</div>
                  <div data-testid="revenue-breakup-detailed-information__forecast-amount-value" class="title font-weight-bold">{{ formatCurrency(selectedRevenueDetail._raw_forecast_amount || selectedRevenueDetail.forecast_amount) }}</div>
                </v-card>
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <v-row>
              <v-col cols="12" md="6">
                <div class="detail-item mb-3">
                  <span class="detail-label">Actual vs {{ proformaShortformLabel }} Variance:</span>
                  <span data-testid="revenue-breakup-detailed-information__actual-vs-proforma-variance" class="detail-value font-weight-bold" :class="(selectedRevenueDetail._raw_actual_vs_proforma_variance_amount || selectedRevenueDetail.actual_vs_proforma_variance_amount) >= 0 ? 'success--text' : 'error--text'">
                    {{ formatCurrency(selectedRevenueDetail._raw_actual_vs_proforma_variance_amount || selectedRevenueDetail.actual_vs_proforma_variance_amount) }}
                    ({{ selectedRevenueDetail.actual_vs_proforma_variance_percent?.toFixed(2) }}%)
                  </span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="detail-item mb-3">
                  <span class="detail-label">Actual vs {{ forecastShortformLabel }} Variance:</span>
                  <span data-testid="revenue-breakup-detailed-information__actual-vs-forecast-variance" class="detail-value font-weight-bold" :class="(selectedRevenueDetail._raw_actual_vs_forecast_variance_amount || selectedRevenueDetail.actual_vs_forecast_variance_amount) >= 0 ? 'success--text' : 'error--text'">
                    {{ formatCurrency(selectedRevenueDetail._raw_actual_vs_forecast_variance_amount || selectedRevenueDetail.actual_vs_forecast_variance_amount) }}
                    ({{ selectedRevenueDetail.actual_vs_forecast_variance_percent?.toFixed(2) }}%)
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Subscription Metrics Card -->
        <v-card data-testid="revenue-breakup-detailed-information__subscription-metrics-card" class="mb-4 elevation-2">
          <v-card-title class="info white--text py-3">
            <v-icon data-testid="revenue-breakup-detailed-information__subscription-metrics-icon" class="mr-2" color="white">mdi-chart-line</v-icon>
            Subscription Metrics
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Current Period Metrics -->
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__current-period-card" outlined class="pa-3 metrics-card metrics-card-current">
                  <div class=" mb-3 primary--text font-weight-bold text-center">Current Period</div>
                  <div class="metric-row">
                    <span class="metric-label">Subscription Count:</span>
                    <span data-testid="revenue-breakup-detailed-information__current-subscription-count" class="metric-value font-weight-bold ml-6">{{ selectedRevenueDetail.current_subscription_count || 0 }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">Cancellation Rate:</span>
                    <span data-testid="revenue-breakup-detailed-information__current-cancellation-rate" class="metric-value ml-6">{{ selectedRevenueDetail.current_cancellation_rate?.toFixed(2) || 0 }}%</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">Retention Rate:</span>
                    <span data-testid="revenue-breakup-detailed-information__current-retention-rate" class="metric-value success--text font-weight-bold ml-6">{{ selectedRevenueDetail.current_retention_rate?.toFixed(2) || 0 }}%</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">ARPA (USD):</span>
                    <span data-testid="revenue-breakup-detailed-information__current-arpa" class="metric-value ml-6">${{ selectedRevenueDetail.current_arpa_usd?.toFixed(2) || 0 }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">LTV (USD):</span>
                    <span data-testid="revenue-breakup-detailed-information__current-ltv" class="metric-value font-weight-bold ml-6">${{ formatNumber(selectedRevenueDetail.current_ltv_usd) }}</span>
                  </div>
                </v-card>
              </v-col>
              
              <!-- Previous Period Metrics -->
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__previous-period-card" outlined class="pa-3 metrics-card metrics-card-previous">
                  <div class=" mb-3 secondary--text font-weight-bold text-center">Previous Period</div>
                  <div class="metric-row">
                    <span class="metric-label">Subscription Count:</span>
                    <span data-testid="revenue-breakup-detailed-information__previous-subscription-count" class="metric-value font-weight-bold ml-6">{{ selectedRevenueDetail.previous_subscription_count || 0 }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">Cancellation Rate:</span>
                    <span data-testid="revenue-breakup-detailed-information__previous-cancellation-rate" class="metric-value ml-6">{{ selectedRevenueDetail.previous_cancellation_rate?.toFixed(2) || 0 }}%</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">Retention Rate:</span>
                    <span data-testid="revenue-breakup-detailed-information__previous-retention-rate" class="metric-value success--text font-weight-bold ml-6">{{ selectedRevenueDetail.previous_retention_rate?.toFixed(2) || 0 }}%</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">ARPA (USD):</span>
                    <span data-testid="revenue-breakup-detailed-information__previous-arpa" class="metric-value ml-6">${{ selectedRevenueDetail.previous_arpa_usd?.toFixed(2) || 0 }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">LTV (USD):</span>
                    <span data-testid="revenue-breakup-detailed-information__previous-ltv" class="metric-value font-weight-bold ml-6">${{ formatNumber(selectedRevenueDetail.previous_ltv_usd) }}</span>
                  </div>
                </v-card>
              </v-col>

              <!-- Forecast Metrics -->
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__forecast-period-card" outlined class="pa-3 metrics-card metrics-card-forecast">
                  <div class=" mb-3 warning--text font-weight-bold text-center">Forecast</div>
                  <div class="metric-row">
                    <span class="metric-label">Subscription Count:</span>
                    <span data-testid="revenue-breakup-detailed-information__forecast-subscription-count" class="metric-value font-weight-bold ml-6">{{ selectedRevenueDetail.forecast_count || 0 }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">Cancellation Rate:</span>
                    <span data-testid="revenue-breakup-detailed-information__forecast-cancellation-rate" class="metric-value ml-6">{{ selectedRevenueDetail.forecast_cancellation_rate?.toFixed(2) || 0 }}%</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">Retention Rate:</span>
                    <span data-testid="revenue-breakup-detailed-information__forecast-retention-rate" class="metric-value success--text font-weight-bold ml-6">{{ selectedRevenueDetail.forecast_retention_rate?.toFixed(2) || 0 }}%</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">ARPA (USD):</span>
                    <span data-testid="revenue-breakup-detailed-information__forecast-arpa" class="metric-value ml-6">${{ selectedRevenueDetail.forecast_arpa_usd?.toFixed(2) || 0 }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="metric-row">
                    <span class="metric-label">LTV (USD):</span>
                    <span data-testid="revenue-breakup-detailed-information__forecast-ltv" class="metric-value font-weight-bold ml-6">${{ formatNumber(selectedRevenueDetail.forecast_ltv_usd) }}</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Trend Indicators -->
            <v-row>
              <v-col cols="12">
                <div class=" mb-3 font-weight-bold">Period Comparison (Current vs Previous)</div>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__subscription-growth-card" outlined class="pa-3 text-center hover-card">
                  <v-icon data-testid="revenue-breakup-detailed-information__subscription-growth-icon" size="32" :color="subscriptionGrowthColor">
                    {{ subscriptionGrowthIcon }}
                  </v-icon>
                  <div class="caption grey--text mt-2">Subscription Growth</div>
                  <div data-testid="revenue-breakup-detailed-information__subscription-growth-value" class="title font-weight-bold mt-1" :class="subscriptionGrowthClass">
                    {{ subscriptionGrowthText }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__retention-change-card" outlined class="pa-3 text-center hover-card">
                  <v-icon data-testid="revenue-breakup-detailed-information__retention-change-icon" size="32" :color="retentionChangeColor">
                    {{ retentionChangeIcon }}
                  </v-icon>
                  <div class="caption grey--text mt-2">Retention Change</div>
                  <div data-testid="revenue-breakup-detailed-information__retention-change-value" class="title font-weight-bold mt-1" :class="retentionChangeClass">
                    {{ retentionChangeText }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__ltv-growth-card" outlined class="pa-3 text-center hover-card">
                  <v-icon data-testid="revenue-breakup-detailed-information__ltv-growth-icon" size="32" :color="ltvGrowthColor">
                    {{ ltvGrowthIcon }}
                  </v-icon>
                  <div class="caption grey--text mt-2">LTV Growth</div>
                  <div data-testid="revenue-breakup-detailed-information__ltv-growth-value" class="title font-weight-bold mt-1" :class="ltvGrowthClass">
                    {{ ltvGrowthText }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Current vs Forecast Comparison -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class=" mb-3 font-weight-bold">Period Comparison (Current vs Forecast)</div>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__subscription-vs-forecast-card" outlined class="pa-3 text-center hover-card">
                  <v-icon data-testid="revenue-breakup-detailed-information__subscription-vs-forecast-icon" size="32" :color="subscriptionVsForecastColor">
                    {{ subscriptionVsForecastIcon }}
                  </v-icon>
                  <div class="caption grey--text mt-2">Subscription vs Forecast</div>
                  <div data-testid="revenue-breakup-detailed-information__subscription-vs-forecast-value" class="title font-weight-bold mt-1" :class="subscriptionVsForecastClass">
                    {{ subscriptionVsForecastText }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__retention-vs-forecast-card" outlined class="pa-3 text-center hover-card">
                  <v-icon data-testid="revenue-breakup-detailed-information__retention-vs-forecast-icon" size="32" :color="retentionVsForecastColor">
                    {{ retentionVsForecastIcon }}
                  </v-icon>
                  <div class="caption grey--text mt-2">Retention vs Forecast</div>
                  <div data-testid="revenue-breakup-detailed-information__retention-vs-forecast-value" class="title font-weight-bold mt-1" :class="retentionVsForecastClass">
                    {{ retentionVsForecastText }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card data-testid="revenue-breakup-detailed-information__ltv-vs-forecast-card" outlined class="pa-3 text-center hover-card">
                  <v-icon data-testid="revenue-breakup-detailed-information__ltv-vs-forecast-icon" size="32" :color="ltvVsForecastColor">
                    {{ ltvVsForecastIcon }}
                  </v-icon>
                  <div class="caption grey--text mt-2">LTV vs Forecast</div>
                  <div data-testid="revenue-breakup-detailed-information__ltv-vs-forecast-value" class="title font-weight-bold mt-1" :class="ltvVsForecastClass">
                    {{ ltvVsForecastText }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn data-testid="revenue-breakup-detailed-information__close-button" color="primary" small @click="handleClose"><p>Close</p></v-btn>
    </template>
  </lb-dialog>
</template>

<script>
export default {
  name: 'RevenueBreakupDetailedInformation',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedRevenueDetail: {
      type: Object,
      default: null
    },
    proformaShortformLabel: {
      type: String,
      default: 'Proforma'
    },
    forecastShortformLabel: {
      type: String,
      default: 'Forecast'
    },
    tablecolors: {
      type: Object,
      default: () => ({
        heading: {
          a: '#1976D2',
          b: '#FF9800'
        }
      })
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    // Current vs Previous calculations
    subscriptionGrowthDiff() {
      if (!this.selectedRevenueDetail) return 0;
      return this.selectedRevenueDetail.current_subscription_count - this.selectedRevenueDetail.previous_subscription_count;
    },
    subscriptionGrowthColor() {
      return this.subscriptionGrowthDiff >= 0 ? 'success' : 'error';
    },
    subscriptionGrowthIcon() {
      return this.subscriptionGrowthDiff >= 0 ? 'mdi-trending-up' : 'mdi-trending-down';
    },
    subscriptionGrowthClass() {
      return this.subscriptionGrowthDiff >= 0 ? 'success--text' : 'error--text';
    },
    subscriptionGrowthText() {
      const sign = this.subscriptionGrowthDiff >= 0 ? '+' : '';
      return `${sign}${this.subscriptionGrowthDiff}`;
    },
    retentionChangeDiff() {
      if (!this.selectedRevenueDetail) return 0;
      return this.selectedRevenueDetail.current_retention_rate - this.selectedRevenueDetail.previous_retention_rate;
    },
    retentionChangeColor() {
      return this.retentionChangeDiff >= 0 ? 'success' : 'error';
    },
    retentionChangeIcon() {
      return this.retentionChangeDiff >= 0 ? 'mdi-trending-up' : 'mdi-trending-down';
    },
    retentionChangeClass() {
      return this.retentionChangeDiff >= 0 ? 'success--text' : 'error--text';
    },
    retentionChangeText() {
      const sign = this.retentionChangeDiff >= 0 ? '+' : '';
      return `${sign}${this.retentionChangeDiff?.toFixed(2)}%`;
    },
    ltvGrowthDiff() {
      if (!this.selectedRevenueDetail) return 0;
      return this.selectedRevenueDetail.current_ltv_usd - this.selectedRevenueDetail.previous_ltv_usd;
    },
    ltvGrowthColor() {
      return this.ltvGrowthDiff >= 0 ? 'success' : 'error';
    },
    ltvGrowthIcon() {
      return this.ltvGrowthDiff >= 0 ? 'mdi-trending-up' : 'mdi-trending-down';
    },
    ltvGrowthClass() {
      return this.ltvGrowthDiff >= 0 ? 'success--text' : 'error--text';
    },
    ltvGrowthText() {
      const sign = this.ltvGrowthDiff >= 0 ? '+' : '';
      return `${sign}$${this.formatNumber(this.ltvGrowthDiff)}`;
    },
    // Current vs Forecast calculations
    subscriptionVsForecastDiff() {
      if (!this.selectedRevenueDetail) return 0;
      return this.selectedRevenueDetail.current_subscription_count - this.selectedRevenueDetail.forecast_count;
    },
    subscriptionVsForecastColor() {
      return this.subscriptionVsForecastDiff >= 0 ? 'success' : 'error';
    },
    subscriptionVsForecastIcon() {
      return this.subscriptionVsForecastDiff >= 0 ? 'mdi-trending-up' : 'mdi-trending-down';
    },
    subscriptionVsForecastClass() {
      return this.subscriptionVsForecastDiff >= 0 ? 'success--text' : 'error--text';
    },
    subscriptionVsForecastText() {
      const sign = this.subscriptionVsForecastDiff >= 0 ? '+' : '';
      return `${sign}${this.subscriptionVsForecastDiff}`;
    },
    retentionVsForecastDiff() {
      if (!this.selectedRevenueDetail) return 0;
      return this.selectedRevenueDetail.current_retention_rate - this.selectedRevenueDetail.forecast_retention_rate;
    },
    retentionVsForecastColor() {
      return this.retentionVsForecastDiff >= 0 ? 'success' : 'error';
    },
    retentionVsForecastIcon() {
      return this.retentionVsForecastDiff >= 0 ? 'mdi-trending-up' : 'mdi-trending-down';
    },
    retentionVsForecastClass() {
      return this.retentionVsForecastDiff >= 0 ? 'success--text' : 'error--text';
    },
    retentionVsForecastText() {
      const sign = this.retentionVsForecastDiff >= 0 ? '+' : '';
      return `${sign}${this.retentionVsForecastDiff?.toFixed(2)}%`;
    },
    ltvVsForecastDiff() {
      if (!this.selectedRevenueDetail) return 0;
      return this.selectedRevenueDetail.current_ltv_usd - this.selectedRevenueDetail.forecast_ltv_usd;
    },
    ltvVsForecastColor() {
      return this.ltvVsForecastDiff >= 0 ? 'success' : 'error';
    },
    ltvVsForecastIcon() {
      return this.ltvVsForecastDiff >= 0 ? 'mdi-trending-up' : 'mdi-trending-down';
    },
    ltvVsForecastClass() {
      return this.ltvVsForecastDiff >= 0 ? 'success--text' : 'error--text';
    },
    ltvVsForecastText() {
      const sign = this.ltvVsForecastDiff >= 0 ? '+' : '';
      return `${sign}$${this.formatNumber(this.ltvVsForecastDiff)}`;
    }
  },
  methods: {
    handleClose() {
      this.$emit('input', false);
      this.$emit('close');
    },
    formatCurrency(value) {
      return this.$nova.formatCurrency(value, '', false, true);
    },
    formatNumber(value) {
      return this.$nova.formatNumber(value);
    }
  }
}
</script>

<style scoped>
.header-card {
  border-left: 5px solid #1976D2 !important;
}

.metric-card-success {
  border-left: 4px solid #4CAF50 !important;
}

.metric-card-proforma {
  border-left: 4px solid #1976D2 !important;
}

.metric-card-forecast {
  border-left: 4px solid #FF9800 !important;
}

.metrics-card {
  height: 100%;
}

.metrics-card-current {
  border-top: 3px solid #1976D2 !important;
}

.metrics-card-previous {
  border-top: 3px solid #757575 !important;
}

.metrics-card-forecast {
  border-top: 3px solid #FF9800 !important;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-weight: 500;
  color: #666;
}

.detail-value {
  text-align: right;
}

.metrics-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.metrics-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.metric-label {
  font-size: 0.875rem;
  color: #666;
}

.metric-value {
  font-size: 0.875rem;
  color: #333;
}

.hover-card {
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: default;
}

.hover-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.15);
}
</style>
